/* src/pages/Register.tsx - PÁGINA DE REGISTRO */
/* ========================================= */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Phone, Building, Eye, EyeOff, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!acceptTerms) {
      setError('Debes aceptar los términos y condiciones');
      return;
    }

    setIsLoading(true);

    try {
      const success = await register(formData);
      if (success) {
        navigate('/perfil');
      } else {
        setError('Error al crear la cuenta. Inténtalo de nuevo.');
      }
    } catch (err) {
      setError('Error al registrarse. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-infinity-black flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-infinity-yellow rounded-full flex items-center justify-center">
              <UserPlus size={32} className="text-infinity-black" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-infinity-yellow">Crear Cuenta</h1>
          <p className="text-infinity-white/80 mt-2">Únete a la comunidad InfinityWits</p>
        </div>
        {/* Formulario */}
        <div className="bg-infinity-dark rounded-lg shadow-lg p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Nombre completo *
              </label>
              <div className="relative">
                <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Tu nombre completo"
                  className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Email *
              </label>
              <div className="relative">
                <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Teléfono
              </label>
              <div className="relative">
                <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+51 921 259 616"
                  className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Empresa
              </label>
              <div className="relative">
                <Building size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Nombre de tu empresa"
                  className="w-full pl-10 pr-4 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Contraseña *
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full pl-10 pr-12 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow hover:text-infinity-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-infinity-yellow mb-2">
                Confirmar contraseña *
              </label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Repite tu contraseña"
                  className="w-full pl-10 pr-12 py-3 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-infinity-yellow hover:text-infinity-white"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex items-start">
              <input
                id="accept-terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="h-4 w-4 text-infinity-yellow focus:ring-infinity-yellow border-infinity-yellow rounded mt-1"
              />
              <label htmlFor="accept-terms" className="ml-2 block text-sm text-infinity-white/80">
                Acepto los{' '}
                <button type="button" className="text-infinity-yellow hover:text-infinity-white">
                  términos y condiciones
                </button>{' '}
                y la{' '}
                <button type="button" className="text-infinity-yellow hover:text-infinity-white">
                  política de privacidad
                </button>
              </label>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 text-lg bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-infinity-white/80">
              ¿Ya tienes una cuenta?{' '}
              <Link to="/login" className="text-infinity-yellow hover:text-infinity-white font-medium">
                Inicia sesión aquí
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
